import {AccountSettingsPanel} from '@common/auth/ui/account-settings/account-settings-panel';
import {AccountSettingsId} from '@common/auth/ui/account-settings/account-settings-sidenav';
import {Trans} from '@common/i18n/trans';
import {Button} from '@common/ui/buttons/button';
import {apiClient} from '@common/http/query-client';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {showHttpErrorToast} from '@common/utils/http/show-http-error-toast';
import {toast} from '@common/ui/toast/toast';
import {message} from '@common/i18n/message';
import {useTrans} from '@common/i18n/use-trans';
import {TelegramIcon} from '@common/icons/social/telegram';
import {TextField} from '@common/ui/forms/input-field/text-field/text-field';
import {ExternalLink} from '@common/ui/buttons/external-link';
import {useState} from 'react';

const TELEGRAM_BOT_USERNAME = 'ypage_bot';

export interface AccountTelegramChat {
  id: number;
  user_id: number;
  label: string | null;
  is_default: boolean;
  telegram_user_id: string;
  telegram_chat_id: string | null;
  chat_type: string | null;
  chat_title: string | null;
  is_connected: boolean;
  connected_at: string | null;
  link_code: string;
}

const ACCOUNT_TELEGRAM_CHATS_KEY = ['account-telegram-chats'];

/**
 * Добавлено: 2026-06-04 — панель привязки Telegram-чатов в настройках аккаунта.
 */
export function TelegramConnectionPanel() {
  const {trans} = useTrans();
  const queryClient = useQueryClient();
  const [newChatLabel, setNewChatLabel] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);

  const chatsQuery = useQuery({
    queryKey: ACCOUNT_TELEGRAM_CHATS_KEY,
    queryFn: async () => {
      const response = await apiClient.get<{chats: AccountTelegramChat[]}>(
        'account/telegram-chats',
      );
      return response.data.chats;
    },
  });

  const createMutation = useMutation({
    mutationFn: async (label: string) => {
      const response = await apiClient.post<{chat: AccountTelegramChat}>(
        'account/telegram-chats',
        {label: label || undefined},
      );
      return response.data.chat;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ACCOUNT_TELEGRAM_CHATS_KEY});
      setNewChatLabel('');
      setShowAddForm(false);
      toast(message('Telegram chat added'));
    },
    onError: error => showHttpErrorToast(error),
  });

  const chats = chatsQuery.data ?? [];
  const connectedChats = chats.filter(c => c.is_connected);
  const pendingChats = chats.filter(c => !c.is_connected);

  return (
    <AccountSettingsPanel
      id={AccountSettingsId.TelegramConnection}
      title={<Trans message="Telegram chats" />}
    >
      <div className="space-y-16 max-w-640">
        <p className="text-sm text-muted">
          <Trans
            values={{
              a: parts => (
                <ExternalLink href={`https://t.me/${TELEGRAM_BOT_USERNAME}`}>
                  {parts}
                </ExternalLink>
              ),
            }}
            message="Connect one or more Telegram chats to receive form leads. Open our bot <a>@ypage_bot</a> in a private chat or add it to a group, then send the link code in that chat to connect. You can assign a chat per site in project form settings."
          />
        </p>

        {connectedChats.length > 0 ? (
          <div className="space-y-10">
            <h3 className="text-sm font-medium">
              <Trans message="Connected chats" />
            </h3>
            <ul className="space-y-10">
              {connectedChats.map(chat => (
                <TelegramChatCard
                  key={chat.id}
                  chat={chat}
                  onUpdated={() =>
                    queryClient.invalidateQueries({
                      queryKey: ACCOUNT_TELEGRAM_CHATS_KEY,
                    })
                  }
                />
              ))}
            </ul>
          </div>
        ) : null}

        {pendingChats.length > 0 ? (
          <div className="space-y-10">
            <h3 className="text-sm font-medium">
              <Trans message="Awaiting connection" />
            </h3>
            <ul className="space-y-10">
              {pendingChats.map(chat => (
                <TelegramChatCard
                  key={chat.id}
                  chat={chat}
                  onUpdated={() =>
                    queryClient.invalidateQueries({
                      queryKey: ACCOUNT_TELEGRAM_CHATS_KEY,
                    })
                  }
                />
              ))}
            </ul>
          </div>
        ) : null}

        {chats.length === 0 && !chatsQuery.isLoading ? (
          <p className="text-sm text-muted">
            <Trans message="No Telegram chats yet. Add a chat and send the link code to the bot." />
          </p>
        ) : null}

        {showAddForm ? (
          <div className="flex flex-col gap-10 sm:flex-row sm:items-end">
            <TextField
              className="flex-auto"
              label={<Trans message="Chat label (optional)" />}
              placeholder={trans({message: 'Sales team'})}
              value={newChatLabel}
              onChange={e => setNewChatLabel(e.target.value)}
            />
            <div className="flex gap-8">
              <Button
                variant="flat"
                color="primary"
                disabled={createMutation.isPending}
                onClick={() => createMutation.mutate(newChatLabel.trim())}
              >
                <Trans message="Create link code" />
              </Button>
              <Button
                variant="text"
                onClick={() => {
                  setShowAddForm(false);
                  setNewChatLabel('');
                }}
              >
                <Trans message="Cancel" />
              </Button>
            </div>
          </div>
        ) : (
          <Button
            variant="flat"
            color="primary"
            size="sm"
            startIcon={<TelegramIcon />}
            onClick={() => setShowAddForm(true)}
          >
            <Trans message="Add Telegram chat" />
          </Button>
        )}
      </div>
    </AccountSettingsPanel>
  );
}

interface TelegramChatCardProps {
  chat: AccountTelegramChat;
  onUpdated: () => void;
}

function TelegramChatCard({chat, onUpdated}: TelegramChatCardProps) {
  const {trans} = useTrans();
  const regenerateMutation = useMutation({
    mutationFn: async () => {
      const response = await apiClient.put<{chat: AccountTelegramChat}>(
        `account/telegram-chats/${chat.id}`,
        {regenerate_link_code: true},
      );
      return response.data.chat;
    },
    onSuccess: () => {
      onUpdated();
      toast(message('Telegram link code updated'));
    },
    onError: error => showHttpErrorToast(error),
  });

  const disconnectMutation = useMutation({
    mutationFn: async () => {
      await apiClient.put(`account/telegram-chats/${chat.id}`, {
        disconnect: true,
      });
    },
    onSuccess: () => {
      onUpdated();
      toast(message('Telegram chat disconnected'));
    },
    onError: error => showHttpErrorToast(error),
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      await apiClient.delete(`account/telegram-chats/${chat.id}`);
    },
    onSuccess: () => {
      onUpdated();
      toast(message('Telegram chat removed'));
    },
    onError: error => showHttpErrorToast(error),
  });

  const testMutation = useMutation({
    mutationFn: async () => {
      await apiClient.post(`account/telegram-chats/${chat.id}/test-message`);
    },
    onSuccess: () => toast(message('Telegram test message sent')),
    onError: error => showHttpErrorToast(error),
  });

  const title = getChatTitle(chat, trans);

  return (
    <li className="rounded border border-divider bg-paper p-12 space-y-10">
      <div className="flex flex-wrap items-center gap-8">
        <span className="font-medium text-sm">{title}</span>
        {chat.is_connected && chat.chat_type ? (
          <span className="rounded bg-chip px-6 py-2 text-xs">
            {formatChatType(chat.chat_type, trans)}
          </span>
        ) : null}
        {chat.is_connected ? (
          <span className="rounded bg-positive/15 px-6 py-2 text-xs text-positive">
            <Trans message="Connected" />
          </span>
        ) : (
          <span className="rounded bg-warning/15 px-6 py-2 text-xs text-warning">
            <Trans message="Not connected" />
          </span>
        )}
      </div>

      {!chat.is_connected ? (
        <>
          <p className="text-sm text-muted">
            <Trans message="Open your Telegram bot and send this code in a private chat or in a group (add the bot to the group first)." />
          </p>
          <div className="rounded bg-chip p-10 font-mono text-sm select-all">
            {chat.link_code}
          </div>
        </>
      ) : (
        <p className="text-xs text-muted">
          <Trans
            message="Chat ID: :id"
            values={{id: chat.telegram_chat_id ?? '—'}}
          />
        </p>
      )}

      <div className="flex flex-wrap gap-8">
        {!chat.is_connected ? (
          <>
            <Button
              variant="flat"
              size="sm"
              disabled={regenerateMutation.isPending}
              onClick={() => regenerateMutation.mutate()}
            >
              <Trans message="Generate new code" />
            </Button>
            <Button
              variant="text"
              size="sm"
              color="danger"
              disabled={deleteMutation.isPending}
              onClick={() => deleteMutation.mutate()}
            >
              <Trans message="Remove" />
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="outline"
              size="sm"
              disabled={testMutation.isPending}
              onClick={() => testMutation.mutate()}
            >
              <Trans message="Send test message" />
            </Button>
            <Button
              variant="flat"
              size="sm"
              color="danger"
              disabled={disconnectMutation.isPending}
              onClick={() => disconnectMutation.mutate()}
            >
              <Trans message="Disconnect" />
            </Button>
            <Button
              variant="text"
              size="sm"
              color="danger"
              disabled={deleteMutation.isPending}
              onClick={() => deleteMutation.mutate()}
            >
              <Trans message="Remove" />
            </Button>
          </>
        )}
      </div>
    </li>
  );
}

function getChatTitle(
  chat: AccountTelegramChat,
  trans: (msg: {message: string}) => string,
): string {
  if (chat.label?.trim()) {
    return chat.label.trim();
  }
  if (chat.chat_title?.trim()) {
    return chat.chat_title.trim();
  }
  if (chat.is_connected) {
    return `Chat ${chat.id}`;
  }
  return trans({message: 'New chat'});
}

function formatChatType(
  type: string,
  trans: (msg: {message: string}) => string,
): string {
  if (type === 'private') return trans({message: 'Private'});
  if (type === 'group' || type === 'supergroup') {
    return trans({message: 'Group'});
  }
  if (type === 'channel') return trans({message: 'Channel'});
  return type;
}
